import React from "react";
import {
  MoreVertical,
  Copy,
  FolderInput,
  CheckCircle2,
  Tag,
  GitBranch,
  FileSpreadsheet,
  SlidersHorizontal,
} from "lucide-react";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

export const ImageCardMenu = ({ asset, prompt = "", className = "" }) => {
  if (!asset || asset.type !== "image") return null;

  const cleanPrompt = (prompt || asset.prompt || asset.meta?.prompt || asset.params?.prompt || "").trim();
  const hasPrompt = cleanPrompt.length > 0;

  const handleCopyPrompt = async (e) => {
    e?.stopPropagation?.();
    if (!hasPrompt) return;
    try {
      await navigator.clipboard.writeText(cleanPrompt);
      toast.success("Prompt copiado al portapapeles");
    } catch (err) {
      console.error("Error al copiar prompt:", err);
      toast.error("No se pudo copiar el prompt");
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid={`action-more-${asset.id}`}
          aria-label="Más opciones"
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.stopPropagation();
            }
          }}
          className={`v-card-menu-touch absolute top-2 right-2 z-20 flex h-7 w-7 items-center justify-center rounded-md border border-[#383838] bg-[#161616] text-[#e8e6e3] shadow-lg opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100 hover:border-[#f5b544] hover:bg-[#262626] hover:text-[#f5b544] focus-visible:border-[#f5b544] focus-visible:bg-[#262626] focus-visible:text-[#f5b544] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#f5b544]/50 data-[state=open]:border-[#f5b544] data-[state=open]:bg-[#262626] data-[state=open]:text-[#f5b544] transition-all duration-150 active:scale-95 ${className}`}
        >
          <MoreVertical size={16} strokeWidth={2.2} className="shrink-0 text-current transition-colors" />
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        side="bottom"
        sideOffset={6}
        className="w-56 border border-[#303030] bg-[#141414]/95 p-1.5 text-[#f4f1ec] shadow-2xl backdrop-blur-md"
        onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
      >
        {/* 1. Copy prompt */}
        <DropdownMenuItem
          data-testid={`menu-copy-prompt-${asset.id}`}
          disabled={!hasPrompt}
          onSelect={handleCopyPrompt}
          className={`flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-xs transition-colors outline-none ${
            hasPrompt
              ? "cursor-pointer text-[#d8d4cd] hover:bg-[#f5b544]/15 hover:text-[#f5b544] focus:bg-[#f5b544]/15 focus:text-[#f5b544]"
              : "cursor-not-allowed text-[#6b6b6b] opacity-50"
          }`}
        >
          <Copy size={14} className={hasPrompt ? "text-[#a3a3a3]" : "text-[#6b6b6b]"} />
          <span>Copy prompt</span>
          {!hasPrompt && (
            <span className="ml-auto text-[10px] text-[#6b6b6b] font-medium">Sin prompt</span>
          )}
        </DropdownMenuItem>

        <DropdownMenuSeparator className="my-1 border-t border-[#262626]" />

        {/* 2. Move asset */}
        <DropdownMenuItem
          disabled
          data-testid={`menu-move-${asset.id}`}
          className="flex items-center justify-between gap-2.5 rounded-md px-2.5 py-1.5 text-xs text-[#6b6b6b] opacity-50 cursor-not-allowed outline-none select-none"
        >
          <div className="flex items-center gap-2.5">
            <FolderInput size={14} />
            <span>Move asset</span>
          </div>
          <span className="text-[10px] text-[#6b6b6b] font-medium">Próximamente</span>
        </DropdownMenuItem>

        {/* 3. Set status */}
        <DropdownMenuItem
          disabled
          data-testid={`menu-status-${asset.id}`}
          className="flex items-center justify-between gap-2.5 rounded-md px-2.5 py-1.5 text-xs text-[#6b6b6b] opacity-50 cursor-not-allowed outline-none select-none"
        >
          <div className="flex items-center gap-2.5">
            <CheckCircle2 size={14} />
            <span>Set status</span>
          </div>
          <span className="text-[10px] text-[#6b6b6b] font-medium">Próximamente</span>
        </DropdownMenuItem>

        {/* 4. Add tag */}
        <DropdownMenuItem
          disabled
          data-testid={`menu-tag-${asset.id}`}
          className="flex items-center justify-between gap-2.5 rounded-md px-2.5 py-1.5 text-xs text-[#6b6b6b] opacity-50 cursor-not-allowed outline-none select-none"
        >
          <div className="flex items-center gap-2.5">
            <Tag size={14} />
            <span>Add tag</span>
          </div>
          <span className="text-[10px] text-[#6b6b6b] font-medium">Próximamente</span>
        </DropdownMenuItem>

        <DropdownMenuSeparator className="my-1 border-t border-[#262626]" />

        {/* 5. View lineage */}
        <DropdownMenuItem
          disabled
          data-testid={`menu-lineage-${asset.id}`}
          className="flex items-center justify-between gap-2.5 rounded-md px-2.5 py-1.5 text-xs text-[#6b6b6b] opacity-50 cursor-not-allowed outline-none select-none"
        >
          <div className="flex items-center gap-2.5">
            <GitBranch size={14} />
            <span>View lineage</span>
          </div>
          <span className="text-[10px] text-[#6b6b6b] font-medium">Próximamente</span>
        </DropdownMenuItem>

        {/* 6. Export metadata */}
        <DropdownMenuItem
          disabled
          data-testid={`menu-export-${asset.id}`}
          className="flex items-center justify-between gap-2.5 rounded-md px-2.5 py-1.5 text-xs text-[#6b6b6b] opacity-50 cursor-not-allowed outline-none select-none"
        >
          <div className="flex items-center gap-2.5">
            <FileSpreadsheet size={14} />
            <span>Export metadata</span>
          </div>
          <span className="text-[10px] text-[#6b6b6b] font-medium">Próximamente</span>
        </DropdownMenuItem>

        {/* 7. Fields */}
        <DropdownMenuItem
          disabled
          data-testid={`menu-fields-${asset.id}`}
          className="flex items-center justify-between gap-2.5 rounded-md px-2.5 py-1.5 text-xs text-[#6b6b6b] opacity-50 cursor-not-allowed outline-none select-none"
        >
          <div className="flex items-center gap-2.5">
            <SlidersHorizontal size={14} />
            <span>Fields</span>
          </div>
          <span className="text-[10px] text-[#6b6b6b] font-medium">Próximamente</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ImageCardMenu;
